import { FeatureFlagName } from 'util/featureFlags'
import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import { getContiguousRows } from '../../bom_generators/PerRowBomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../../MountingSystemAbstract'
import {
  CompatibilityParameters,
  Fastener,
  Item,
  MountingCalcResult,
  MountingComponentType,
  RoofTypeName,
} from '../../types'
import { addItems, createMountingItem } from '../../utils'
import {
  BRACKET_FOOT_TYPES,
  computeAllSetsTotals,
  extractSetsFromRows,
  getContiguousColumns,
  getDeviceCountOnRailForBlocks,
  getStringCountForBlocks,
  getStrungModuleCountForBlocks,
  getSystemFromBlocks,
  type FootTypeKey,
} from './NovaComputations'

const INTER_PANEL_GAP = 50

const ROOF_TYPE_TO_FOOT_TYPE: Record<string, FootTypeKey> = {
  'Metal Tin': 'L-Feet',
  'Flat Concrete': 'L-Feet',
  'Tile Clay': 'Tile Hook',
  'Tile Concrete': 'Tile Hook',
  Kliplock: 'Mini Kliplok Bracket',
  'Metal Standing Seam': 'Longline',
  Trapezoidal: 'Trap Bracket',
}

const FOOT_TYPE_TO_SKU: Record<FootTypeKey, string> = {
  'L-Feet': 'NOVA-TRB-F16-RFN',
  'Tile Hook': 'NOVA-TRH-F01-RFN',
  'Full size Kliplok Bracket': 'NOVA-TRB-C08',
  'Mini Kliplok Bracket': 'NOVA-TRB-C12',
  Longline: 'NOVA-TRB-C16',
  'Trap Bracket': 'NOVA-TRB-B06',
}

const SILVER_TO_BLACK: Record<string, string> = {
  'NOVA-RA-NOV-4700': 'NOVA-RA-NOV-4700-B',
  'NOVA-PC-NEC-40-NS6': 'NOVA-PC-NEC-40-NS6-B',
  'NOVA-PC-NIC35-NS6': 'NOVA-PC-NIC35-NS6-B',
  'NOVA-TRB-F16-RFN': 'NOVA-TRB-F16-RFN-B',
  'NOVA-TRH-F01-RFN': 'NOVA-TRH-A53-RFN-B',
}

const TILE_ROOF_TYPES: RoofTypeName[] = ['Tile Clay', 'Tile Concrete']

class NovaSpacingAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet: SpacingRuleSet = {
    interPanel: { horizontalSpacing: INTER_PANEL_GAP, verticalSpacing: INTER_PANEL_GAP },
  }
}

function resolveColour(sku: string, colour: string): string {
  if (colour !== 'Black') return sku
  return SILVER_TO_BLACK[sku] ?? sku
}

function emitItems(code: string, qty: number, type: MountingComponentType, blockIndex: number): Item[] {
  const items: Item[] = []
  const template: Item = {
    name: code,
    components: [{ name: code, type, left: 0, top: 0 }],
  }
  for (let i = 0; i < qty; i++) {
    items.push(createMountingItem(template, { blockIndex }))
  }
  return items
}

function emitRoofAttachment(
  items: Item[],
  footType: FootTypeKey,
  feetTotal: number,
  roofBracketsTotal: number,
  colour: string,
  tiltOption: string,
  blockIndex: number
): void {
  const baseSku = FOOT_TYPE_TO_SKU[footType]
  if (!baseSku) return

  if (footType === 'L-Feet' || footType === 'Tile Hook') {
    if (!(footType === 'L-Feet' && tiltOption !== 'No tilt')) {
      items.push(...emitItems(resolveColour(baseSku, colour), feetTotal, MountingComponentType.ROOF_HOOK, blockIndex))
    }
  } else {
    items.push(...emitItems(baseSku, roofBracketsTotal, MountingComponentType.ROOF_HOOK, blockIndex))
    if (tiltOption === 'No tilt' && BRACKET_FOOT_TYPES.includes(footType)) {
      const lFeetSku = resolveColour(FOOT_TYPE_TO_SKU['L-Feet'], colour)
      items.push(...emitItems(lFeetSku, roofBracketsTotal, MountingComponentType.ROOF_HOOK, blockIndex))
    }
  }
}

function emitTiltComponents(
  items: Item[],
  tiltOption: string,
  feetTotal: number,
  colour: string,
  blockIndex: number
): void {
  if (tiltOption === 'No tilt') return

  switch (tiltOption) {
    case '10-15 deg': {
      const halfFeet = Math.ceil(feetTotal / 2)
      items.push(...emitItems('NOVA-TRB-AFL-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      items.push(...emitItems('NOVA-TRB-ARL-F15-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      break
    }
    case '15-30 deg': {
      const halfFeet = Math.ceil(feetTotal / 2)
      items.push(...emitItems('NOVA-TRB-AFL-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      items.push(...emitItems('NOVA-TRB-ARL-F30-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      break
    }
    case '30-60 deg': {
      const halfFeet = Math.ceil(feetTotal / 2)
      items.push(...emitItems('NOVA-TRB-AFL-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      items.push(...emitItems('NOVA-TRB-ARL-F60-RFN', halfFeet, MountingComponentType.OTHER, blockIndex))
      break
    }
    case 'A-frame (15-30 deg)':
      items.push(...emitItems('NOVA-TRB-AFK-A4S-RCN', Math.ceil(feetTotal / 2), MountingComponentType.OTHER, blockIndex))
      break
  }
}

export class NovaBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    const options = this.input.options
    const channels = Number(options.channels) || 2
    const channelLength = Number(options.railLength) === 2400 ? 2400 : 4700
    const footSpacing = Number(options.footSpacing) || 1200
    const extraLength = Number(options.extraLength) || 0
    const colour = (options.componentColour as string) || 'Silver'
    const roofType = this.input.roof?.roofTypeName as string | undefined
    const corrugated = roofType === 'Metal Tin' && options.corrugatedRoof === 'Yes'
    const footType =
      roofType === 'Kliplock'
        ? (options.kliplokBracket as FootTypeKey) || 'Mini Kliplok Bracket'
        : roofType
          ? ROOF_TYPE_TO_FOOT_TYPE[roofType]
          : 'L-Feet'
    const isTileRoof = TILE_ROOF_TYPES.includes(roofType as RoofTypeName)
    const tiltOption = (options.tiltOption as string) || 'No tilt'

    const block = this.block
    const panel = block.panel
    const isParallelToLongSide = options.railOrientation === 'Parallel to long side'
    const panelWidth = isParallelToLongSide ? panel.height : panel.width
    const panelSpacing = panelWidth + INTER_PANEL_GAP

    const isPortrait = block.orientation === 'portrait'
    const isRailHorizontal = isPortrait ? !isParallelToLongSide : isParallelToLongSide

    let sets: { panelsPerRow: number; numRows: number }[]
    if (isRailHorizontal) {
      const contiguousRuns = block.rows.flatMap(getContiguousRows)
      sets = extractSetsFromRows(contiguousRuns)
    } else {
      const contiguousRuns = getContiguousColumns(block.rows)
      sets = extractSetsFromRows(contiguousRuns)
    }
    if (sets.length === 0) return result

    const totals = computeAllSetsTotals(
      sets,
      panelSpacing,
      footSpacing,
      channelLength,
      extraLength,
      channels,
      corrugated,
      footType,
      tiltOption
    )

    const bi = this.blockIndex
    const railSku = resolveColour(channelLength === 4700 ? 'NOVA-RA-NOV-4700' : 'NOVA-RA-NOV-2400', colour)
    const items: Item[] = []

    if (totals.railsTotal > 0) items.push(...emitItems(railSku, totals.railsTotal, MountingComponentType.RAIL, bi))
    if (totals.joinersTotal > 0)
      items.push(...emitItems('NOVA-SK-NOV', totals.joinersTotal, MountingComponentType.SPLICE, bi))
    if (totals.endClampsTotal > 0)
      items.push(
        ...emitItems(resolveColour('NOVA-PC-NEC-40-NS6', colour), totals.endClampsTotal, MountingComponentType.CLAMP, bi)
      )
    if (totals.midClampsTotal > 0)
      items.push(
        ...emitItems(resolveColour('NOVA-PC-NIC35-NS6', colour), totals.midClampsTotal, MountingComponentType.CLAMP, bi)
      )
    if (totals.earthingLugsTotal > 0)
      items.push(...emitItems('NOVA-AC-ELG03', totals.earthingLugsTotal, MountingComponentType.OTHER, bi))

    const system = getSystemFromBlocks([block])
    const numStrings = getStringCountForBlocks([block], system)
    const strungModules = getStrungModuleCountForBlocks([block], system)
    const totalPanelClips = totals.panelClips + 2 * strungModules
    if (totalPanelClips > 0) items.push(...emitItems('NOVA-AC-PCC', totalPanelClips, MountingComponentType.OTHER, bi))

    if (totals.cableClips > 0)
      items.push(...emitItems('NOVA-AC-PCC08', totals.cableClips, MountingComponentType.OTHER, bi))
    if (totals.endCapsTotal > 0)
      items.push(...emitItems('NOVA-AC-AEC', totals.endCapsTotal, MountingComponentType.OTHER, bi))
    if (totals.arcRubber > 0) items.push(...emitItems('NOVA-ARC', totals.arcRubber, MountingComponentType.OTHER, bi))

    if (footType && totals.feetTotal > 0) {
      emitRoofAttachment(items, footType, totals.feetTotal, totals.roofBracketsTotal, colour, tiltOption, bi)
    }

    if (!isTileRoof && channels !== 3 && totals.feetTotal > 0) {
      emitTiltComponents(items, tiltOption, totals.feetTotal, colour, bi)
    }

    const numOptimisers = getDeviceCountOnRailForBlocks([block], system)
    if (numOptimisers > 0) {
      items.push(...emitItems('SLCN', numOptimisers, MountingComponentType.OTHER, bi))
    }

    if (options.useIsolatorCovers === 'Yes' && numStrings > 0) {
      items.push(...emitItems('NOVA-IBC', numStrings, MountingComponentType.OTHER, bi))
    }

    return addItems(result, items)
  }
}

export class Nova extends MountingSystemAbstract {
  layoutAdjusterClass = NovaSpacingAdjuster
  bomGeneratorClass = NovaBomGenerator

  getFastener(): Fastener {
    return {
      includedInRoofHookProduct: true,
      name: '',
      qtyPerRoofHook: 0,
      length: 0,
      diameter: 0,
      components: [],
    }
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypes: [
        'Flat Concrete',
        'Kliplock',
        'Metal Standing Seam',
        'Metal Tin',
        'Tile Clay',
        'Tile Concrete',
        'Trapezoidal',
      ],
      roofTypeRequired: true,
      featureFlag: 'mounting_nova' as FeatureFlagName,
    }
  }
}

export { computeAllSetsTotals, computePerSet, extractSetsFromRows, getContiguousColumns } from './NovaComputations'
export {
  getStringCountForBlocks,
  getStrungModuleCountForBlocks,
  getDeviceCountOnRailForBlocks,
} from './NovaComputations'
